import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SplitModal } from '@/core/modal';
import { useTracker } from '@/core/tracking';
import { usePriceService } from '@/layout/appWrapper/ServiceProvider';
import beforeAfter from '@/public/images/covers/before_after.webp';
import { PaymentStrategiesIds } from '@/services/paymentService';
import { PaywallOfferPageVariant } from '@/services/settingsService/settingsTypes';
import {
  offerPageIntroPriceIdState,
  offerPageVariantState,
  onboardingState,
} from '@/stores/onboardingStore';
import settingsState from '@/stores/settingsStore';
import { useLocalization } from '@/services/localizationService';
import { useUser } from '@/services/userService';
import usePaymentStrategies from '../paymentStrategies/usePaymentStrategies';
import { getPaywallPreviousAction } from './paywallOfferPageVariant';

type OfferId = 'weekly' | 'yearly' | 'welcome' | 'free';

export type OfferPageSelection = {
  selectedOffer: OfferId;
  isFreeTrialEnabled: boolean;
};

type Props = {
  id: string;
  variant: Exclude<PaywallOfferPageVariant, 'control'>;
  previousAction?: string;
  initialSelection?: OfferPageSelection;
  shouldTrackShown?: boolean;
  onShown?: () => void;
  onContinue: (selection: OfferPageSelection) => void;
  onOtherPlans: (selection: OfferPageSelection) => void;
  onDismiss: () => void;
};

export default function OfferPageModal({
  id,
  variant,
  previousAction,
  initialSelection,
  shouldTrackShown = true,
  onShown,
  onContinue,
  onOtherPlans,
  onDismiss,
}: Props) {
  const { data: user, isLoading: isUserLoading } = useUser();
  const { t, data } = useLocalization();
  const { track } = useTracker();
  const settings = useRecoilValue(settingsState);
  const setOnboarding = useSetRecoilState(onboardingState);
  const setOfferPageIntroPriceId = useSetRecoilState(
    offerPageIntroPriceIdState
  );
  const setOfferPageVariant = useSetRecoilState(offerPageVariantState);
  const { usePaywallPrices, useFormatRecurringPrice, usePricesByIds } =
    usePriceService();
  const paywallPrices = usePaywallPrices();
  const introPriceId = settings.values.ocp2wIntroPriceId;
  const { data: introPrices } = usePricesByIds(
    variant === 'ocp' && introPriceId ? [introPriceId] : []
  );
  const introPrice = introPrices?.[0];
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(
    initialSelection?.selectedOffer ||
      (variant === 'pcp' ? 'weekly' : 'welcome')
  );
  const [isFreeTrialEnabled, setIsFreeTrialEnabled] = useState(
    initialSelection?.isFreeTrialEnabled || false
  );
  const isFreeTrialEnabledRef = useRef(isFreeTrialEnabled);
  const didNavigateRef = useRef(false);
  const hasTrackedShownRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  const trackRef = useRef(track);
  const variantRef = useRef(variant);
  onDismissRef.current = onDismiss;
  trackRef.current = track;
  variantRef.current = variant;

  const paymentStrategies = usePaymentStrategies({
    selectedPeriod: 'weekly',
    remotePaymentStrategies: settings.values.paymentStrategies,
    isUpgrade: false,
    userPricePeriod: 'weekly',
  });
  const businessStrategy = paymentStrategies.find(
    (strategy) => strategy.id === PaymentStrategiesIds.Business
  );
  const weeklyPrice = useFormatRecurringPrice(paywallPrices?.business.weekly);
  const yearlyPrice = useFormatRecurringPrice(paywallPrices?.business.yearly);
  const currency = paywallPrices?.business.weekly.currency || 'usd';
  const welcomeOfferPrice =
    useFormatRecurringPrice(introPrice).split(/\/| every /)[0];
  const freeTrialPrice = useFormatRecurringPrice({
    amountCents: 0,
    currency,
    recurringInterval: 'week',
    recurringIntervalCount: 1,
  }).split('/')[0];
  const isFreeTrialAvailable = Boolean(user?.monetization?.trialAvailable);
  const isWelcomeOfferAvailable = Boolean(introPrice && isFreeTrialAvailable);
  const isOcpPreview =
    variant === 'ocp' &&
    process.env.NODE_ENV === 'development' &&
    !introPriceId;

  const offeredPriceIds = useMemo(() => {
    const priceIds = [settings.values.paymentStrategies.business.weekly.price];
    if (variant === 'pcp') {
      priceIds.push(settings.values.paymentStrategies.business.yearly.price);
    } else if (introPriceId) {
      priceIds.push(introPriceId);
    }
    return priceIds;
  }, [introPriceId, settings.values.paymentStrategies.business, variant]);

  useEffect(() => {
    if (!shouldTrackShown || hasTrackedShownRef.current) return;

    track('paywall', 'shown', {
      previousAction: getPaywallPreviousAction(previousAction),
      priceIds: offeredPriceIds,
      type: variant,
    });
    hasTrackedShownRef.current = true;
    onShown?.();
  }, [
    offeredPriceIds,
    onShown,
    previousAction,
    shouldTrackShown,
    track,
    variant,
  ]);

  useEffect(
    () => () => {
      if (didNavigateRef.current) return;

      trackRef.current(
        variantRef.current,
        'dismissed',
        variantRef.current === 'pcp'
          ? { freeTrial: isFreeTrialEnabledRef.current }
          : undefined
      );
      onDismissRef.current();
    },
    []
  );

  const chooseOffer = useCallback(
    (offer: OfferId) => {
      setSelectedOffer(offer);
      if (variant === 'pcp') {
        if (offer === 'weekly' || offer === 'yearly') {
          track('pcp', `${offer}_tapped`, {
            freeTrial: isFreeTrialEnabled,
          });
        }
        return;
      }

      track('ocp', offer === 'welcome' ? 'intro_tapped' : 'ft_plan_tapped');
    },
    [isFreeTrialEnabled, track, variant]
  );

  const selectPayment = useCallback(
    (
      period: 'weekly' | 'yearly',
      freeTrial: boolean,
      offerIntroPriceId: string | null,
      selection: OfferPageSelection
    ) => {
      if (!businessStrategy) return;

      setOnboarding({
        isFreeTrialEnabled: freeTrial,
        isReminderEnabled: false,
        selectedPeriod: period,
        selectedPaymentStrategy: businessStrategy,
      });
      setOfferPageIntroPriceId(offerIntroPriceId);
      setOfferPageVariant(variant);
      didNavigateRef.current = true;
      onContinue(selection);
    },
    [
      businessStrategy,
      onContinue,
      setOfferPageIntroPriceId,
      setOfferPageVariant,
      setOnboarding,
      variant,
    ]
  );

  const continueToCheckout = useCallback(() => {
    if (isOcpPreview && selectedOffer === 'welcome') return;

    if (variant === 'pcp') {
      const period = selectedOffer === 'yearly' ? 'yearly' : 'weekly';
      const selection = { selectedOffer, isFreeTrialEnabled };
      track('pcp', 'continue_tapped', {
        periodicity: period === 'yearly' ? 360 : 7,
        freeTrial: isFreeTrialEnabled,
      });
      selectPayment(period, isFreeTrialEnabled, null, selection);
      return;
    }

    const isWelcomeOffer = selectedOffer === 'welcome';
    if (isWelcomeOffer && !isWelcomeOfferAvailable) return;

    track('ocp', 'continue_tapped', { planChosen: selectedOffer });
    selectPayment(
      'weekly',
      !isWelcomeOffer,
      isWelcomeOffer ? introPriceId || null : null,
      { selectedOffer, isFreeTrialEnabled }
    );
  }, [
    introPriceId,
    isWelcomeOfferAvailable,
    isOcpPreview,
    isFreeTrialEnabled,
    selectPayment,
    selectedOffer,
    track,
    variant,
  ]);

  const openOtherPlans = useCallback(() => {
    track(
      variant,
      'others_tapped',
      variant === 'pcp' ? { freeTrial: isFreeTrialEnabled } : undefined
    );
    setOfferPageIntroPriceId(null);
    setOfferPageVariant('control');
    didNavigateRef.current = true;
    onOtherPlans({ selectedOffer, isFreeTrialEnabled });
  }, [
    isFreeTrialEnabled,
    onOtherPlans,
    setOfferPageIntroPriceId,
    setOfferPageVariant,
    track,
    variant,
  ]);

  const toggleFreeTrial = useCallback(() => {
    const nextValue = !isFreeTrialEnabled;
    isFreeTrialEnabledRef.current = nextValue;
    setIsFreeTrialEnabled(nextValue);
    if (nextValue) track('pcp', 'ft_tapped');
  }, [isFreeTrialEnabled, track]);

  return (
    <SplitModal
      id={id}
      label={data.common.paywallOfferPageTitle}
      className="!w-full max-w-[610px] !p-0 !overflow-hidden"
      additionalStyle={{
        zoom: 'min(1, calc((100vh - 2rem) / 900px))',
      }}
      closeBtnClassName="!top-3 !start-3"
    >
      <div className="relative w-full bg-[#f2f2f4] text-black">
        <div className="relative aspect-video overflow-hidden bg-black">
          <img
            className="w-full h-full object-contain"
            src={beforeAfter.src}
            alt=""
          />
          <div className="absolute inset-x-4 bottom-4 flex justify-between text-white">
            <span className="rounded-full bg-overlay-black-70 px-3 py-1 SB14">
              Before
            </span>
            <span className="rounded-full bg-overlay-black-70 px-3 py-1 SB14">
              After
            </span>
          </div>
        </div>
        <div className="px-8 pt-5 pb-7">
          <header className="text-center">
            <h2 className="B32">{t(data.common.paywallOfferPageTitle)}</h2>
            <p className="M16 text-overlay-black-60 mt-1">
              {t(data.common.paywallOfferPageSubtitle)}
            </p>
          </header>

          {variant === 'pcp' && isFreeTrialAvailable && (
            <button
              type="button"
              className={`w-full min-h-[64px] mt-5 px-4 py-2 rounded-xl border-2 text-start flex items-center justify-between ${
                isFreeTrialEnabled
                  ? 'border-primary-accent bg-primary-accent-100'
                  : 'border-gray-200 bg-white'
              }`}
              onClick={toggleFreeTrial}
              disabled={!isFreeTrialAvailable}
            >
              <span className="flex flex-col justify-center">
                <span className="block SB18">
                  {isFreeTrialEnabled
                    ? t(data.common.paywallOfferPageFreeAccessEnabled)
                    : t(data.common.paywallOfferPageNotSureYet)}
                </span>
                {!isFreeTrialEnabled && (
                  <span className="block M14 text-overlay-black-60 mt-0.5">
                    {t(data.common.paywallOfferPageEnableFreeAccess)}
                  </span>
                )}
              </span>
              <span
                className={`w-5 h-5 rounded-full border-2 ${
                  isFreeTrialEnabled
                    ? 'bg-primary-accent border-primary-accent'
                    : 'border-gray-400'
                }`}
              >
                {isFreeTrialEnabled && (
                  <span className="block text-center text-white SB14 leading-4">
                    ✓
                  </span>
                )}
              </span>
            </button>
          )}

          <div
            className={`${
              variant === 'ocp' ? 'space-y-2.5 mt-6' : 'space-y-2 mt-5'
            }`}
          >
            {variant === 'pcp' ? (
              <>
                <OfferOption
                  label={t(data.common.weekly)}
                  price={weeklyPrice}
                  trialEnabled={isFreeTrialEnabled}
                  emphasized
                  capitalizeLabel
                  selected={selectedOffer === 'weekly'}
                  onClick={() => chooseOffer('weekly')}
                />
                <OfferOption
                  label={t(data.common.yearly)}
                  price={yearlyPrice}
                  trialEnabled={isFreeTrialEnabled}
                  emphasized
                  capitalizeLabel
                  selected={selectedOffer === 'yearly'}
                  onClick={() => chooseOffer('yearly')}
                />
              </>
            ) : (
              <>
                <OfferOption
                  label={t(data.common.paywallOfferPageWelcomeOffer)}
                  detail={{
                    duration: t(data.common.paywallOfferPageDays, {
                      days: (introPrice?.recurringIntervalCount || 2) * 7,
                    }),
                    price: welcomeOfferPrice,
                  }}
                  large
                  selected={selectedOffer === 'welcome'}
                  onClick={() => chooseOffer('welcome')}
                  disabled={!isWelcomeOfferAvailable && !isOcpPreview}
                />
                <OfferOption
                  label={t(data.common.paywallOfferPageTrialPlan)}
                  subLabel={t(data.common.paywallOfferPageTryNowForFree)}
                  detail={{
                    duration: t(data.common.paywallOfferPageDays, {
                      days: 7,
                    }),
                    price: `${freeTrialPrice}/${
                      weeklyPrice.split('/')[1] || 'week'
                    }`,
                  }}
                  large
                  selected={selectedOffer === 'free'}
                  onClick={() => chooseOffer('free')}
                  disabled={
                    !isOcpPreview && !isUserLoading && !isFreeTrialAvailable
                  }
                />
              </>
            )}
          </div>

          <button
            type="button"
            className={`w-full btn btn--xl btn--black shadow-dark-button mt-7 ${
              variant === 'ocp' ? 'min-h-[66px]' : ''
            }`}
            onClick={continueToCheckout}
            disabled={
              !businessStrategy ||
              (variant === 'ocp' &&
                selectedOffer === 'welcome' &&
                !isWelcomeOfferAvailable &&
                !isOcpPreview)
            }
          >
            {t(data.common.continue)}
          </button>
          {variant === 'ocp' && (
            <p className="M15 text-center mt-2 text-overlay-black-60">
              {selectedOffer === 'free' ? (
                <>
                  <strong className="text-black">
                    {t(data.common.paywallOfferPageFreeForDays, { days: 7 })}
                  </strong>
                  , {t(data.common.paywallOfferPageThen, null, 'lowercase')}{' '}
                  <strong className="text-black">{weeklyPrice}</strong>
                </>
              ) : (
                <>
                  <strong className="text-black">{welcomeOfferPrice}</strong>{' '}
                  <strong className="text-black">
                    {t(data.common.paywallOfferPageForFirstDays, {
                      days: (introPrice?.recurringIntervalCount || 2) * 7,
                    })}
                  </strong>
                  , {t(data.common.paywallOfferPageThen, null, 'lowercase')}{' '}
                  <strong className="text-black">{weeklyPrice}</strong>
                </>
              )}
            </p>
          )}
          <button
            type="button"
            className="w-full mt-6 underline SB16"
            onClick={openOtherPlans}
          >
            {t(data.common.paywallOfferPageOtherPlans)}
          </button>
        </div>
      </div>
    </SplitModal>
  );
}

function OfferOption({
  label,
  badge,
  price,
  trialEnabled,
  detail,
  subLabel,
  emphasized = false,
  capitalizeLabel = false,
  large = false,
  selected,
  onClick,
  disabled,
}: {
  label: ReactNode;
  badge?: ReactNode;
  price?: string;
  trialEnabled?: boolean;
  detail?: { duration: ReactNode; price: string };
  subLabel?: ReactNode;
  emphasized?: boolean;
  capitalizeLabel?: boolean;
  large?: boolean;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const { t, data } = useLocalization();
  const offerBadge = trialEnabled
    ? t(data.common.paywallOfferPageWeekFree)
    : badge;

  return (
    <button
      type="button"
      className={`relative w-full ${
        large ? 'min-h-[75px] px-[18px]' : 'min-h-[64px] px-4'
      } rounded-xl border-2 flex items-center justify-between text-start ${
        selected ? 'border-primary-accent bg-white' : 'border-gray-200 bg-white'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="flex flex-col">
        <span
          className={`${large || emphasized ? 'SB18' : 'SB16'} ${
            capitalizeLabel ? 'capitalize' : ''
          } compact:!text-[20px]`}
        >
          {label}
        </span>
        {subLabel && (
          <span className="M14 text-primary-accent">{subLabel}</span>
        )}
      </span>
      {detail ? (
        <span className="ms-auto flex flex-col text-end">
          <span className="SB18 text-primary-accent">{detail.duration}</span>
          <span className="M14 text-overlay-black-60">
            {t(data.common.paywallOfferPageAtPrice, { price: detail.price })}
          </span>
        </span>
      ) : (
        offerBadge && (
          <span
            className={`absolute start-1/2 -translate-x-1/2 ${
              emphasized ? 'M13' : 'M12'
            } px-2 py-1 rounded-md border ${
              selected || trialEnabled
                ? 'border-primary-accent text-primary-accent'
                : 'border-gray-200 text-overlay-black-60'
            }`}
          >
            {offerBadge}
          </span>
        )
      )}
      {price && !detail && (
        <PriceLabel
          price={price}
          showThen={trialEnabled}
          emphasized={emphasized}
        />
      )}
    </button>
  );
}

function PriceLabel({
  price,
  showThen,
  emphasized,
}: {
  price: string;
  showThen?: boolean;
  emphasized: boolean;
}) {
  const { t, data } = useLocalization();
  const [amount, interval] = price.split('/');

  return (
    <span className={`ms-auto ${emphasized ? 'M14' : 'M13'}`}>
      {showThen && <>{t(data.common.paywallOfferPageThen)} </>}
      <strong className="SB16">{amount}</strong>
      {interval && `/${interval}`}
    </span>
  );
}
